import { Component, EventEmitter, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ConstantsService } from '../../services/constants/constants.service';

export interface PackageApiSettings {
  employment_verification?: {
    verification_years?: string;
    verification_emp_min?: string;
    verification_emp_max?: string;
  };
  credential_verification?: {
    verification_cred_min?: string;
    verification_cred_max?: string;
  };
  aml?: {
    fuzziness?: string;
  };
  education_verification?: {
    verification_type?: string;
  };
  certn_directorship_check?: {
    country?: string;
  };
  icrc?: {
    employment_sector?: string;
  };
  ukdbsc?: {
    employment_sector?: string;
  };
  ncc_police_check?: {
    type?: string;
  };
}

@Component({
  selector: 'app-package-api-settings-modal',
  templateUrl: './package-api-settings-modal.component.pug',
  styleUrls: ['./package-api-settings-modal.component.scss'],
  standalone: false,
})
export class PackageApiSettingsModalComponent implements OnInit {
  // Section visibility, set by the opener via BsModalService initialState.
  showCoveragePeriod = false;
  showEmployers = false;
  showCredentials = false;
  showAml = false;
  showEducation = false;
  showDirectorship = false;
  showIcrc = false;
  showUkdbsc = false;
  showNccPoliceCheck = false;

  // Saved settings passed in for the edit / "Update API settings" flow.
  initialSettings: PackageApiSettings = {};

  title = $localize`API settings for background checks`;
  subtitle = $localize`Set the default package settings used for API requests.`;

  coverageOptions = ConstantsService.CENTRIC_EMPLOYMENT_COVERAGE.map((option) => ({
    value: `${option.value} years`,
    key: option.value,
  }));
  employersMinOptions = ConstantsService.CENTRIC_EMPLOYERS_QUANTITY_MIN;
  employersMaxOptions = ConstantsService.CENTRIC_EMPLOYERS_QUANTITY_MAX;
  credentialsMinOptions = ConstantsService.CENTRIC_CREDENTIALS_QUANTITY_MIN;
  credentialsMaxOptions = ConstantsService.CENTRIC_CREDENTIALS_QUANTITY_MAX;
  fuzzinessOptions = ConstantsService.FUZZINESSOPTIONS;
  fuzzinessTooltip = ConstantsService.FUZZINESS_TOOLTIP;
  educationScopeOptions = ConstantsService.CENTRIC_EDUCATION_SCOPE;
  countryOptions = ConstantsService.DIRECTORSHIP_COUNTRIES;
  countryPlaceholder = ConstantsService.DIRECTORSHIP_COUNTRY_PLACEHOLDER;
  employmentSectorOptions = ConstantsService.EMPLOYMENT_SECTOR;
  employmentSectorPlaceholder = $localize`Select employment sector`;
  employmentTypeOptions = ConstantsService.EMPLOYMENT_TYPE;

  // Current selections seeded with the locked package defaults.
  verificationYears = '5';
  verificationEmpMin = '2';
  verificationEmpMax = '3';
  verificationCredMin = '1';
  verificationCredMax = '3';
  fuzzinessKey = 'exact_match';
  fuzzinessLabel = 'Exact match';
  verificationType = 'highest';
  educationScopeLabel = 'Highest education';
  nccType = ConstantsService.EMPLOYMENT_TYPE[0].key;

  // No package default exists for these; they stay empty until the user picks one.
  directorshipCountry: string | null = null;
  icrcEmploymentSector: string | null = null;
  ukdbscEmploymentSector: string | null = null;

  onSave: EventEmitter<PackageApiSettings> = new EventEmitter<PackageApiSettings>();
  onCancel: EventEmitter<void> = new EventEmitter<void>();

  constructor(public _bsModalRef: BsModalRef) {}

  ngOnInit(): void {
    const employment = this.initialSettings?.employment_verification || {};
    if (employment.verification_years) {
      this.verificationYears = employment.verification_years;
    }
    if (employment.verification_emp_min) {
      this.verificationEmpMin = employment.verification_emp_min;
    }
    if (employment.verification_emp_max) {
      this.verificationEmpMax = employment.verification_emp_max;
    }

    const credential = this.initialSettings?.credential_verification || {};
    if (credential.verification_cred_min) {
      this.verificationCredMin = credential.verification_cred_min;
    }
    if (credential.verification_cred_max) {
      this.verificationCredMax = credential.verification_cred_max;
    }

    const aml = this.initialSettings?.aml || {};
    if (aml.fuzziness) {
      this.fuzzinessKey = aml.fuzziness;
      this.fuzzinessLabel =
        this.fuzzinessOptions.find((option) => option.key === aml.fuzziness)?.value || 'Exact match';
    }

    const education = this.initialSettings?.education_verification || {};
    if (education.verification_type) {
      this.verificationType = ConstantsService.normalizeEducationScope(education.verification_type);
      this.educationScopeLabel =
        this.educationScopeOptions.find((option) => option.key === this.verificationType)?.value ||
        this.educationScopeLabel;
    }

    this.directorshipCountry = this.initialSettings?.certn_directorship_check?.country ?? null;
    this.icrcEmploymentSector = this.initialSettings?.icrc?.employment_sector ?? null;
    this.ukdbscEmploymentSector = this.initialSettings?.ukdbsc?.employment_sector ?? null;
  }

  onCoverageChange($event: any): void {
    this.verificationYears = $event.key;
  }

  onEmployersMinChange($event: any): void {
    this.verificationEmpMin = $event.value;
  }

  onEmployersMaxChange($event: any): void {
    this.verificationEmpMax = $event.value;
  }

  onCredentialsMinChange($event: any): void {
    this.verificationCredMin = $event.value;
  }

  onCredentialsMaxChange($event: any): void {
    this.verificationCredMax = $event.value;
  }

  onFuzzinessChange($event: any): void {
    this.fuzzinessLabel = $event.value;
    this.fuzzinessKey =
      this.fuzzinessOptions.find((option) => option.value === $event.value)?.key || 'exact_match';
  }

  onEducationScopeChange($event: any): void {
    this.educationScopeLabel = $event.value;
    this.verificationType =
      this.educationScopeOptions.find((option) => option.value === $event.value)?.key ||
      this.verificationType;
  }

  onDirectorshipCountryChange($event: any): void {
    this.directorshipCountry = $event.key;
  }

  onIcrcSectorChange($event: any): void {
    this.icrcEmploymentSector = $event.value;
  }

  onUkdbscSectorChange($event: any): void {
    this.ukdbscEmploymentSector = $event.value;
  }

  get isInvalid(): boolean {
    if (this.showEmployers && Number(this.verificationEmpMin) > Number(this.verificationEmpMax)) {
      return true;
    }
    if (this.showCredentials && Number(this.verificationCredMin) > Number(this.verificationCredMax)) {
      return true;
    }
    if (this.showDirectorship && !this.directorshipCountry) {
      return true;
    }
    if (this.showIcrc && !this.icrcEmploymentSector) {
      return true;
    }
    if (this.showUkdbsc && !this.ukdbscEmploymentSector) {
      return true;
    }
    return false;
  }

  buildSettings(): PackageApiSettings {
    const settings: PackageApiSettings = {};

    if (this.showCoveragePeriod || this.showEmployers) {
      const employment: PackageApiSettings['employment_verification'] = {};
      if (this.showCoveragePeriod) {
        employment.verification_years = this.verificationYears;
      }
      if (this.showEmployers) {
        employment.verification_emp_min = this.verificationEmpMin;
        employment.verification_emp_max = this.verificationEmpMax;
      }
      settings.employment_verification = employment;
    }

    if (this.showCredentials) {
      settings.credential_verification = {
        verification_cred_min: this.verificationCredMin,
        verification_cred_max: this.verificationCredMax,
      };
    }

    if (this.showAml) {
      settings.aml = { fuzziness: this.fuzzinessKey };
    }

    if (this.showEducation) {
      settings.education_verification = { verification_type: this.verificationType };
    }

    if (this.showDirectorship && this.directorshipCountry) {
      settings.certn_directorship_check = { country: this.directorshipCountry };
    }

    if (this.showIcrc && this.icrcEmploymentSector) {
      settings.icrc = { employment_sector: this.icrcEmploymentSector };
    }

    if (this.showUkdbsc && this.ukdbscEmploymentSector) {
      settings.ukdbsc = { employment_sector: this.ukdbscEmploymentSector };
    }

    if (this.showNccPoliceCheck) {
      settings.ncc_police_check = { type: this.nccType };
    }

    return settings;
  }

  save(): void {
    if (this.isInvalid) {
      return;
    }
    this.onSave.emit(this.buildSettings());
    this._bsModalRef.hide();
  }

  cancel(): void {
    this._bsModalRef.hide();
    this.onCancel.emit();
  }

  protected readonly ConstantsService = ConstantsService;
}
